<template>
  <div class="cf-page-container">
    <cf-validated-form name="payment">
      <div class="cf-page-container cf-page-container--grid">
        <!-- Left Page Card -->
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <div class="cf-card cf-card--mobile cf-card--tablet cf-flex cf-flex--column cf-flex--gap-16">
            <div class="cf-flex cf-flex--row cf-flex--align-center">
              <div class="cf-flex cf-flex--column cf-flex--gap-8 cf-flex--grow-1">
                <!-- Cart Title -->
                <h1 class="cf-title cf-title--1">
                  {{ content.OrderSummary.title }}
                </h1>

                <!-- Cart Description -->
                <p
                  v-if="content.OrderSummary.description && content.OrderSummary.description.length"
                  class="cf-subtitle cf-subtitle--2"
                >
                  {{ content.OrderSummary.description }}
                </p>
              </div>

              <!-- Table Number Display -->
              <div
                v-if="orderDetails.fulfillmentType === 'dine-in' && tableNumberDisplay"
                class="cf-flex cf-flex--column cf-flex--align-center"
              >
                <h4 class="cf-subtitle cf-subtitle--2">
                  {{ userLocationType.description }}
                </h4>

                <p class="cf-title cf-title--4">
                  {{ tableNumberDisplay }}
                </p>
              </div>
            </div>

            <!-- Upsell Order Items Top (Not to be confused with the upsell items rendered in the upsell modal) -->
            <cf-upsell-order-items v-if="displayUpsellItems && merchant.features['order-summary-move-upsell-to-top']" />

            <p
              v-if="shippingFullNameIsInvalid"
              class="cf-subtitle cf-subtitle--2"
            >
              Full name is required for all shipping orders. Please edit your selected address.
            </p>

            <div class="cf-flex cf-flex--column cf-flex--gap-18">
              <template v-if="orderDetails.fulfillmentType !== 'dine-in'">
                <!-- Location Order Details -->
                <cf-selected-location-order-details
                  :header-title="content.OrderSummary.metadata.location"
                  :edit-success-callback-handler="editOrderDetailsSuccessHandler"
                />

                <!-- Table Number Dispay for Dine In -->
                <template v-if="displayEditableRoomNumber">
                  <cf-text-input
                    v-model="roomNumber"
                    type="text"
                    label="Room Number"
                    name="roomNumber"
                    icon-class="fa-door-closed"
                    :validations="{
                      required: true
                    }"
                  />
                </template>

                <!-- Fulfillment Time Select -->
                <cf-fulfillment-time-select
                  v-if="orderDetails.fulfillmentType !== 'shipping'"
                  ref="timeSelect"
                />

                <!-- Pick up Method Radio Select -->
                <cf-radio-input
                  v-if="selectedLocation.pickupFulfillmentTypes.length > 1"
                  v-model="pickupType"
                  name="pickupType"
                  radio-style="box"
                  label="Pickup Method"
                  :options="computedPickUpTypeRadioOptions"
                  :validations="{ required: true }"
                />
              </template>

              <!-- Shipping Methods, Instructions and Gifting note -->
              <template v-if="orderDetails.fulfillmentType === 'shipping'">
                <cf-shipping-method-selector />

                <cf-add-shipping-instructions v-model="shippingInstructions" />

                <cf-add-shipping-gift-note
                  v-if="merchant.options.shipmentGiftingEnabled"
                  v-model="shippingGiftMessage"
                />
              </template>

              <div class="cf-flex cf-flex--column cf-flex--gap-16">
                <!-- Order Items Summary Title -->
                <order-items-summary-title />

                <div class="cf-flex cf-flex--column cf-flex--gap-16">
                  <!-- Order Items -->
                  <cf-order-items
                    v-if="orderDetails.orderType !== 'group'"
                    :order="order"
                  />

                  <!-- Group Order Details -->
                  <group-order-details v-if="orderDetails.orderType === 'group'" />

                  <!-- Request Utensils Toggler -->
                  <div
                    v-if="showUtensilsToggle"
                    class="cf-padding-top-16 cf-border-top"
                  >
                    <cf-switch-input
                      v-model="includeUtensils"
                      :label="content.OrderSummary.metadata.includeUtensils"
                      name="requestUtensils"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Upsell Items Order Items Bottom (Not to be confused with the upsell items rendered in the upsell modal) -->
          <div
            v-if="displayUpsellItems && !merchant.features['order-summary-move-upsell-to-top']"
            class="cf-card cf-card--mobile cf-card--tablet cf-flex cf-flex--column"
          >
            <cf-upsell-order-items />
          </div>
        </div>

        <!-- Right Page Card -->
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <div class="cf-card cf-card--mobile cf-card--tablet cf-flex cf-flex--column cf-flex--gap-16">
            <h1 class="cf-title cf-title--1 cf-padding-bottom">
              Order Amount
            </h1>

            <!-- Tip Selection -->
            <cf-tip-selection
              v-if="!selectedLocation.features.HidePrices && orderDetails.fulfillmentType !== 'shipping'"
              v-model="tip"
              :label="content.OrderSummary.metadata.tip"
              :total="totalForTipPercentage"
            />

            <template v-if="!selectedLocation.features.HidePrices">
              <!-- Redeemable Checkout Offers -->
              <cf-cart-offers />

              <!-- Apply Promo Code -->
              <cf-apply-discount-code />
            </template>

            <!-- Order Instructions -->
            <cf-text-input
              v-if="merchant.options.orderInstructionsEnabled"
              v-model="orderLevelInstructions"
              type="text"
              :label="orderInstructionsContent"
              name="orderLevelInstructions"
            />

            <!-- Delivery Instructions -->
            <cf-text-input
              v-if="orderDetails.fulfillmentType === 'delivery'"
              v-model="dropOffInstructions"
              type="text"
              label="Delivery Instructions"
              placeholder="Please provide any specific instructions here..."
              name="dropOffInstructions"
            />

            <!-- Order Totals -->
            <cf-order-totals
              v-if="!selectedLocation.features.HidePrices"
              :order="order"
              :totals="{ tip: tip, totalWithTip: totalWithTip }"
            />

            <div
              v-if="!orderValidations.isValid"
              class="cf-card cf-card--error cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10 cf-padding"
            >
              <p
                v-for="error in orderValidations.errors"
                :key="error"
                class="cf-subtitle cf-subtitle--2 cf-subtitle--red-alt cf-subtitle--heavy"
              >
                {{ error }}
              </p>
            </div>

            <!-- Payment Methods Buttons -->
            <cf-payment-methods
              v-if="displayPaymentMethodButtons"
              ref="paymentMethods"
              :confirm-pickup-location="orderDetails.fulfillmentType === 'pick-up' && merchant.options.confirmPickupLocationOnCheckout"
              :disabled="isDisabled"
              :pay-cta-text="content.PaymentScreen.metadata.buttonCta"
              :hidden-elements="{ total: selectedLocation.features.HidePrices }"
              :tip="tip"
              :total="totalWithTip"
              :is-payment-page="true"
              :checkout-handler="checkout"
            />

            <!-- Group Order Checkout Cta's -->
            <template v-if="orderDetails.orderType === 'group'">
              <!-- Host Cancel Group Order -->
              <button
                v-if="order.groupOrder.you.isHost"
                class="cf-button cf-button--danger cf-button--lg cf-button--fill"
                type="button"
                @click="cancelGroupOrder"
              >
                Cancel Order
              </button>

              <template v-else>
                <group-order-locked-message v-if="order.groupOrder.isLocked" />

                <div class="cf-flex cf-flex--column cf-flex--gap-12">
                  <p class="smaller grow">
                    {{ order.groupOrder.host.displayName }} (the host) will checkout and submit the order once everyone <span class="no-wrap">is ready.</span>
                  </p>

                  <!-- Guest Leave Group Order -->
                  <button
                    class="cf-button cf-button--danger cf-button--lg cf-button--fill"
                    type="button"
                    @click="leaveGroupOrder"
                  >
                    Leave Group
                  </button>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
    </cf-validated-form>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfRadioInput from '@/view/components/shared/cf-radio-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfSwitchInput from '@/view/components/shared/cf-switch-input/index.vue';

  import CfUpsellOrderItems from '@/view/components/globals/cf-upsell-order-items/index.vue';
  import CfSelectedLocationOrderDetails from '@/view/components/globals/cf-selected-location-order-details/index.vue';
  import CfFulfillmentTimeSelect from '@/view/components/globals/cf-fulfillment-time-select/index.vue';
  import CfOrderItems from '@/view/components/globals/cf-order-items/index.vue';
  import CfTipSelection from '@/view/components/globals/cf-tip-selection/index.vue';
  import CfOrderTotals from '@/view/components/globals/cf-order-totals/index.vue';
  import CfCartOffers from '@/view/components/globals/cf-cart-offers/index.vue';
  import CfApplyDiscountCode from '@/view/components/globals/cf-apply-discount-code/index.vue';
  import CfShippingMethodSelector from '@/view/components/globals/cf-shipping-method-selector/index.vue';
  import CfAddShippingInstructions from '@/view/components/globals/cf-add-shipping-instructions/index.vue';
  import CfAddShippingGiftNote from '@/view/components/globals/cf-add-shipping-gift-note/index.vue';
  import CfPaymentMethods from '@/view/components/globals/cf-payment-methods/index.vue';

  import OrderItemsSummaryTitle from './order-items-summary-title/index.vue';
  import GroupOrderDetails from './group-order-details/index.vue';


  export default {
    name: 'OrderSummaryPage',

    components: {
      CfUpsellOrderItems,
      CfSelectedLocationOrderDetails,
      CfFulfillmentTimeSelect,
      CfOrderItems,
      CfTipSelection,
      CfOrderTotals,
      CfCartOffers,
      CfApplyDiscountCode,
      CfShippingMethodSelector,
      CfAddShippingInstructions,
      CfAddShippingGiftNote,
      CfPaymentMethods,
      CfRadioInput,
      CfTextInput,
      CfSwitchInput,
      OrderItemsSummaryTitle,
      GroupOrderDetails,
      CfValidatedForm
    },

    data() {
      return {
        dropOffInstructions: null,
        includeUtensils: false,
        lastFetchedOrderWithPickupTime: null,
        orderLevelInstructions: null,
        roomNumber: null,
        shippingGiftMessage: null,
        shippingInstructions: null,
        submittedCheckoutOptions: null,
        selectedPickupType: '',
        tip: 0
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'upsellItems', 'orderItemsUnavailablePerSchedule', 'tableNumber', 'showGroupOrderButton', 'tableNumberDisplay', 'userLocationType', 'orderValidations', 'currentCapiOrderType']),
      ...mapGetters('session', ['orderDetails', 'session', 'currentUser']),
      ...mapGetters('shipping', ['selectedShippingMethod']),

      displayEditableRoomNumber() {
        /** A user can edit their table number if the url does not have an imbedded table number (ie. tb=100) */
        return this.orderDetails.fulfillmentType === 'room-service' && !this.tableNumberDisplay;
      },

      isDisabled() {
        if (!this.orderValidations.isValid || this.shippingFullNameIsInvalid) {
          return true;
        }

        if (this.totalWithTip === 0 && this.currentCapiOrderType !== 'TipOnly') {
          if (this.order.discount || this.selectedLocation.allowNoPaymentInfo) {
            return false;
          }

          return true;
        }

        return false;
      },

      shippingFullNameIsInvalid() {
        return this.orderDetails.fulfillmentType === 'shipping' && !this.orderDetails.deliveryAddress.addressee;
      },

      totalForTipPercentage() {
        let total = this.order.orderItemTotal;

        if (this.merchant.options.calculateTipAfterTax) {
          total += this.order.tax;
        }

        return total;
      },

      totalWithTip() {
        const total = this.tip + this.order.total;

        return Math.round(total * 100) / 100;
      },

      pickupType: {
        get() {
          return this.selectedPickupType;
        },

        set(updatedValue) {
          this.selectedPickupType = updatedValue;

          this.$store.dispatch('session/updateOrderDetails', {
            ...this.orderDetails,
            pickupType: updatedValue
          });
        }
      },

      showUtensilsToggle() {
        return this.selectedLocation.features.IncludeUtensilsEnabled
          && (this.orderDetails.fulfillmentType === 'delivery' || this.orderDetails.fulfillmentType === 'pick-up');
      },

      computedPickUpTypeRadioOptions() {
        const content = this.content.OrderSummary?.metadata;

        const result = this.selectedLocation.pickupFulfillmentTypes.map((type) => {
          if (type.value === 'curbside') return { ...type, display: content.curbsideSelection };
          if (type.value === 'take-out') return { ...type, display: content.insideSelection };
          if (type.value === 'drive-through') return { ...type, display: content.driveThroughSelection };

          return type;
        });

        return result;
      },

      hasNotMetMetUpsellModalTrigger() {
        let cartItemTotal = 0;

        this.order.orderItems.forEach((orderItem) => {
          if (orderItem.units) {
            cartItemTotal += orderItem.quantity * orderItem.units;
          }
          else {
            cartItemTotal += orderItem.quantity;
          }
        });

        const upsellItem = this.order.upsellRules?.find(upsellRule => !!upsellRule.triggerAmount);

        return (this.order.upsellDisplayMode === 'Modal' || this.order.upsellDisplayMode === 'ModalCategory') && cartItemTotal < upsellItem?.triggerAmount;
      },

      orderInstructionsContent() {
        return this.content.OrderLevelInstructions.metadata.inputPlaceholder;
      },

      computedUpsellItems() {
        return this.upsellItems.checkoutUpsellItems;
      },

      displayUpsellItems() {
        return this.computedUpsellItems.length > 0 && this.orderDetails.orderType !== 'group' && (this.order.upsellDisplayMode === 'Checkout' || this.hasNotMetMetUpsellModalTrigger);
      },

      displayPaymentMethodButtons() {
        const isNonGroupOrder = this.orderDetails.orderType !== 'group';
        const isGroupOrderHost = this.orderDetails.orderType === 'group' && this.order.groupOrder?.you?.isHost && this.order.orderItemCount > 0;

        return isNonGroupOrder || isGroupOrderHost;
      }
    },

    watch: {
      'order.orderItemCount': 'orderCountWatcher'
    },

    created() {
      this.createdHandler();

      this.selectedPickupType = this.selectedLocation.features.RequirePickupMethodSelection
        ? ''
        : this.orderDetails.pickupType;
    },

    methods: {
      async createdHandler() {
        await this.$store.dispatch('locations/getLocation', { silent: true, withMenu: true });

        if (this.orderDetails.fulfillmentType === 'room-service') {
          this.roomNumber = this.tableNumber;
        }

        this.orderCountWatcher(this.order.orderItemCount);

        this.triggerEventTracking();
      },

      async checkout(options) {
        this.submittedCheckoutOptions = options;

        const validationResult = await this.$store.dispatch('orderAhead/validateOrderFulfillmentTime');

        if (validationResult?.isTimeslotVerificationRequired || validationResult?.isFutureAsapTimeslotVerificationRequired) {
          const modalPayload = { ...validationResult, confirmClickHandler: this.confirmTime };

          this.$openModal('confirmTime', modalPayload);
        }
        else {
          this.checkoutSubmit(options);
        }
      },

      confirmTime(isConfirmed) {
        if (isConfirmed) {
          this.checkoutSubmit(this.submittedCheckoutOptions);
        }
        else {
          this.submittedCheckoutOptions.onCheckoutError();

          this.$router.push({ name: 'orderAheadCheckoutSummaryPage', params: { merchantId: this.$route.params.merchantId } });
        }
      },

      async checkoutSubmit(options) {
        const pickupTime = await this.$store.dispatch('session/returnActualPickupTime');

        try {
          const body = {
            contactInfo: options.contactInfo,
            orderLevelInstructions: this.orderLevelInstructions,
            paymentMethod: options.paymentMethod,
            pickupDate: pickupTime
          };

          if (this.orderDetails.fulfillmentType === 'delivery') {
            body.deliveryRequest = {
              deliveryService: this.orderDetails.deliveryEstimate.deliveryService,
              deliveryTime: this.orderDetails.deliveryEstimate.deliveryTime,
              tip: this.tip,
              dropOffInstructions: this.dropOffInstructions,
              dropOffLocation: {
                latitude: this.orderDetails.deliveryAddressGeoCoordinates.latitude,
                longitude: this.orderDetails.deliveryAddressGeoCoordinates.longitude
              },
              dropOffAddress: {
                city: this.orderDetails.deliveryAddress.city,
                countryCode: this.orderDetails.deliveryAddress.countryCode,
                line1: this.orderDetails.deliveryAddress.line1,
                line2: this.orderDetails.deliveryAddress.line2,
                postalCode: this.orderDetails.deliveryAddress.postalCode,
                state: this.orderDetails.deliveryAddress.state
              }
            };

            body.includeUtensils = this.includeUtensils;

            body.pickupLocation = 'take-out';
          }

          else if (this.orderDetails.fulfillmentType === 'dine-in') {
            body.tip = this.tip;
            body.pickupLocation = 'dine-in';
            body.sublocationId = this.tableNumber;
          }

          else if (this.orderDetails.fulfillmentType === 'pick-up') {
            body.includeUtensils = this.includeUtensils;
            body.tip = this.tip;
            body.pickupLocation = this.pickupType || this.orderDetails.pickupType;
          }

          else if (this.orderDetails.fulfillmentType === 'room-service') {
            body.tip = this.tip;
            body.pickupLocation = 'room-service';
            body.sublocationId = this.roomNumber;
          }

          else if (this.orderDetails.fulfillmentType === 'shipping') {
            body.shipmentRequest = {
              shippingGiftMessage: this.shippingGiftMessage,
              shippingInstructions: this.shippingInstructions,
              shippingMethod: this.selectedShippingMethod.shippingMethod,
              shippingProvider: this.selectedShippingMethod.shippingProvider,
              shipToAddress: {
                addressee: this.orderDetails.deliveryAddress.addressee,
                city: this.orderDetails.deliveryAddress.city,
                countryCode: this.orderDetails.deliveryAddress.countryCode,
                line1: this.orderDetails.deliveryAddress.line1,
                line2: this.orderDetails.deliveryAddress.line2,
                postalCode: this.orderDetails.deliveryAddress.postalCode,
                state: this.orderDetails.deliveryAddress.state
              }
            };
            body.pickupLocation = 'shipping';

            delete body.pickupDate;
          }

          const payment = await this.$store.dispatch('orders/submitOrder', body);

          // this.$triggerGoogleTrackingEvent('PlaceOrderSuccess', 'Checkout');

          this.$triggerNewRelicEvent('PlaceOrderSuccess', 'Information', 'Place Order Success');

          this.$router.push({
            name: 'orderReceipt',
            params: {
              merchantId: this.$route.params.merchantId,
              receiptToken: payment.receiptToken
            }
          });

          this.$closeModal('cart');
        }

        catch (error) {
          // this.$triggerGoogleTrackingEvent('PlaceOrderFailure', 'Checkout');

          this.$triggerNewRelicEvent('PlaceOrderFailure', 'Error', 'Place Order Failure');

          await this.$store.dispatch('orders/handleSubmitError', {
            error,
            pickupTime
          });

          options.onCheckoutError();

          if (this.$refs.header) {
            this.$refs.header.scrollIntoView();
          }
        }
      },

      orderCountWatcher(orderCount) {
        if (orderCount === 0) {
          this.$openModal('empty-cart-modal', null, { isRequired: true });
        }
      },

      editOrderDetailsSuccessHandler() {
        /** Important!!!
         * This callback is used to invoke the pickup selector's "getPickupDays" method when user updates the selected location, delivery address and/or fulfillmentType.
         * It is invoked after all stateful logic has updated, and completed this component's DOM updates.
         * Cannot use "watcher" in pickup selector as watching for associated state changes creates DOM race update conditions between this "parent" component and the "child" pickup selector component.
        */
        if (this.$refs.timeSelect) {
          this.$refs.timeSelect.getPickupDays();
        }
      },

      triggerEventTracking() {
        const { orderItems, tax, subtotal } = this.order;

        const eventData = { itemInfo: orderItems, paymentInfo: { total: subtotal, tax, tip: this.tip } };

        this.$googleEventTracking.postEvent('view_cart', eventData);
      },

      leaveGroupOrder() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Leave',
          primaryText: 'Your items in the group cart will be removed and your cart will be cleared.',
          onConfirm: () => this.$store.dispatch('orders/groupOrderLeave'),
          onConfirmSuccess: () => {
            this.$closeModal('cart');

            // this.$triggerGoogleTrackingEvent('GuestLeavesGroupOrder', 'GroupOrder');

            this.$triggerNewRelicEvent('GuestLeavesGroupOrder', 'Information', 'Guest Left Group Order');
          },
          title: 'Leave group order?'
        });
      },

      cancelGroupOrder() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Cancel',
          primaryText: 'All items and guests will be removed from your cart.',
          onConfirm: () => this.$store.dispatch('orders/cancelOrder'),
          onConfirmSuccess: () => {
            this.$closeAllModals();
          },
          title: 'Cancel group order?'
        });
      }
    }
  };
</script>
